import { Box, Typography, useTheme } from "@mui/material";
import * as React from "react";
import { JournalctlCard } from "./journal_ctl_card";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchJournalctl } from "../../scripts/api_v2/commands_client";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { Divider } from "rsuite";

function formatDate(d) {
  return (
    ("0" + d.getDate()).slice(-2) +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    d.getFullYear() +
    " " +
    ("0" + d.getHours()).slice(-2) +
    ":" +
    ("0" + d.getMinutes()).slice(-2) +
    ":" +
    ("0" + d.getSeconds()).slice(-2)
  );
}

export function JournalctlLog() {
  const params = useParams();
  const backend = useBackend();
  const [tag, setTag] = React.useState("client");
  const [range, setRange] = React.useState(10);
  const [paused, setPaused] = React.useState(false);

  let {
    data,
    error,
    dataUpdatedAt,
    refetch: refetchJrnlCtl,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.JOURNAL_CTL, backend.fetch, params.site_id, tag, range],
    queryFn: () => fetchJournalctl(backend.fetch, params.site_id, tag, range),
    enabled: false,
  });

  return (
    <>
      <JournalctlCard
        tag={tag}
        setTag={setTag}
        range={range}
        setRange={setRange}
        paused={paused}
        setPaused={setPaused}
        refetchJrnlCtl={refetchJrnlCtl}
      >
        <Log tag={tag} range={range} setRange={setRange} paused={paused} />
        {dataUpdatedAt ? (
          <Typography variant="body2">Last update: {new Date(dataUpdatedAt).toLocaleTimeString()}</Typography>
        ) : null}
      </JournalctlCard>
    </>
  );
}

function Log({ tag, range, setRange, paused }) {
  const [journalctl, setJournalctl] = React.useState(null);
  const theme = useTheme();
  const params = useParams();
  const backend = useBackend();
  const bottomRef = React.useRef();

  let {
    data,
    error,
    isLoading,
    refetch: refetchJrnlCtl,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.JOURNAL_CTL, backend.fetch, params.site_id, tag, range],
    queryFn: () => fetchJournalctl(backend.fetch, params.site_id, tag, range),
    refetchInterval: 10000,
    enabled: !paused,
  });

  React.useEffect(() => {
    if (data) {
      const merged = journalctl
        ? data.concat(
            journalctl.filter(
              (el) => parseInt(el.__MONOTONIC_TIMESTAMP) < parseInt(data[data.length - 1].__MONOTONIC_TIMESTAMP),
            ),
          )
        : data;
      setJournalctl(merged);
      setRange(10);
    }
  }, [data]);

  React.useEffect(() => {
    if (bottomRef.current && journalctl && journalctl.length > 30) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [journalctl]); // Runs when messages update*/

  return (
    <Box
      component="div"
      sx={{ overflow: "auto", flexDirection: "column", display: "flex", maxHeight: "700px" }}
      boxSizing
    >
      {journalctl != null
        ? journalctl
            .slice()
            .reverse()
            .map((el, index) => <LogRow journalRow={el} isFirst={index == 0} />)
        : null}
      <div ref={bottomRef} />
    </Box>
  );
}

function parseMessage(message) {
  const match = message.match(/\[([A-Z]+)\|([A-Z]+)\]\s*(.*)/);
  if (match) {
    return { logger: match[1], logType: match[2], message: match[3] };
  } else {
    return { logger: "", logType: "INFO", message: message };
  }
}

function LogRow({ journalRow, isFirst }) {
  const theme = useTheme();
  const { logger, logType, message } = parseMessage(journalRow.MESSAGE);
  const color =
    logType == "INFO"
      ? "#444"
      : logType == "DEBUG"
        ? "#aaa"
        : logType == "WARNING"
          ? theme.palette.warning.main
          : logType == "ERROR"
            ? theme.palette.error.main
            : "purple";

  return (
    <Typography
      variant="body2"
      key={journalRow.__CURSOR}
      style={{ lineHeight: "1.5em", color: color, whiteSpace: "nowrap", borderTop: !isFirst && "1px solid #ddd" }}
    >
      <b
        style={{
          width: "160px",
          display: "inline-block",
          color: theme.palette.secondary.main,
        }}
      >
        {formatDate(new Date(journalRow.__REALTIME_TIMESTAMP / 1000))}
      </b>
      <b
        style={{
          width: "70px",
          display: "inline-block",
          color: color,
        }}
      >
        {logger}
      </b>
      {message}
    </Typography>
  );
}
